/**
 * /characters/new — create a UGC character profile (issue #589).
 *
 * Keeps the create step lightweight: name + description + optional
 * Higgsfield reference id + traits. The reference image is attached
 * after creation on the detail page (it needs a row id to key the R2
 * upload), so we redirect there on success.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiJson } from '../lib/api';
import type { Character } from '../lib/types';
import {
  TraitsEditor,
  rowsToTraits,
  type TraitRow,
} from '../components/TraitsEditor';

export function NewCharacter() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [higgsfieldId, setHiggsfieldId] = useState('');
  const [traitRows, setTraitRows] = useState<TraitRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        higgsfield_character_id: higgsfieldId.trim() || undefined,
        traits: rowsToTraits(traitRows),
      };
      const res = await apiJson<{ character: Character }>('/api/marketing/characters', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      navigate(`/characters/${res.character.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create character');
    } finally {
      setSubmitting(false);
    }
  }

  const disabled = submitting || !name.trim();

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <div>
          <Link to="/characters" style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            ← Characters
          </Link>
          <h1 style={{ marginTop: 4 }}>New character</h1>
          <p>
            Define a reusable persona. Add the Higgsfield reference id now or paste it later — then
            attach a reference image on the next screen.
          </p>
        </div>
      </header>

      <section className="brief-panel" style={{ maxWidth: 640 }}>
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            value={name}
            placeholder='e.g. "Maya — solo backpacker, 27"'
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Description</span>
          <textarea
            rows={4}
            value={description}
            placeholder="Who is this character? Their backstory, how they speak, what they're into — this gets woven into the script prompt."
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Higgsfield character id (optional)</span>
          <input
            type="text"
            value={higgsfieldId}
            placeholder="Reference token from @higgsfield/cli for clip-to-clip consistency"
            onChange={(e) => setHiggsfieldId(e.target.value)}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </label>

        <h2 style={{ marginTop: 24, fontSize: 18 }}>Traits</h2>
        <TraitsEditor rows={traitRows} onChange={setTraitRows} />

        {error && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-primary" disabled={disabled} onClick={submit}>
            {submitting ? 'Creating…' : 'Create character'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={submitting}
            onClick={() => navigate('/characters')}
          >
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}
